import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import {
  useAccountsWithBalances,
  useClearChartOfAccounts,
  useCreateAccount,
  useDeactivateAccount,
  useDeleteAccount,
  useUpdateAccount,
} from '../../hooks/useAccounts';
import { useBankAccounts } from '../../hooks/useBank';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { formatCurrency, humanizeLabel } from '../../lib/formatters';
import { ApiError } from '../../lib/api';
import {
  ACCOUNT_SUBTYPES,
  ACCOUNT_TYPES,
  NORMAL_BALANCE_MAP,
  type Account,
  type AccountType,
  type AccountWithBalance,
  type CashFlowCategory,
} from '@accounting/shared';

export const Route = createFileRoute('/_auth/accounts/')({
  component: AccountsPage,
});

// Small bank glyph for the "Connected" badge on accounts tied to a bank feed.
function BankIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2L2 7v2h20V7L12 2zM4 10v8H2v2h20v-2h-2v-8h-2v8h-3v-8h-2v8H9v-8H7v8H4v-8z" />
    </svg>
  );
}

/** Exact sum of NUMERIC(19,4) money strings via integer (BigInt) math — never float. */
function sumMoney(values: string[]): string {
  let total = 0n;
  for (const raw of values) {
    const v = raw.trim();
    const neg = v.startsWith('-');
    const [intPart, fracPart = ''] = v.replace('-', '').split('.');
    const frac = (fracPart + '0000').slice(0, 4);
    const scaled = BigInt((intPart || '0') + frac);
    total += neg ? -scaled : scaled;
  }
  const neg = total < 0n;
  const abs = (neg ? -total : total).toString().padStart(5, '0');
  return `${neg ? '-' : ''}${abs.slice(0, -4)}.${abs.slice(-4)}`;
}

function AccountsPage() {
  const companyId = useActiveCompanyId();
  // Fetch inactive accounts too so the "Show only active" toggle has something to reveal.
  const { data: accounts = [], isLoading, error } = useAccountsWithBalances(companyId, {
    includeInactive: true,
  });
  const { data: bankData } = useBankAccounts(companyId);
  const bankByGlAccountId = new Map((bankData?.bankAccounts ?? []).map((b) => [b.accountId, b]));
  const deactivate = useDeactivateAccount(companyId);
  const reactivate = useUpdateAccount(companyId);
  const setReconcilable = useUpdateAccount(companyId);
  const setCheckWriting = useUpdateAccount(companyId);
  const del = useDeleteAccount(companyId);
  const clear = useClearChartOfAccounts(companyId);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Accounts</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  const editingAccount = accounts.find((a) => a.id === editingId) ?? null;

  // ── Build the parent/child tree ─────────────────────────────────────────
  const visible = accounts.filter((a) => !showOnlyActive || a.isActive);
  const byId = new Map(visible.map((a) => [a.id, a]));
  const childrenOf = new Map<string | null, AccountWithBalance[]>();
  for (const a of visible) {
    const pid = a.parentAccountId && byId.has(a.parentAccountId) ? a.parentAccountId : null;
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid)!.push(a);
  }
  for (const list of childrenOf.values()) list.sort((x, y) => x.code.localeCompare(y.code));

  function rolledUp(a: AccountWithBalance, seen: Set<string>): string {
    if (seen.has(a.id)) return a.balance;
    seen.add(a.id);
    const kids = childrenOf.get(a.id) ?? [];
    return sumMoney([a.balance, ...kids.map((k) => rolledUp(k, seen))]);
  }

  const rows: ReactNode[] = [];
  const seen = new Set<string>();
  function emit(a: AccountWithBalance, depth: number) {
    if (seen.has(a.id)) return; // guard against accidental cycles
    seen.add(a.id);
    const kids = childrenOf.get(a.id) ?? [];
    const bank = bankByGlAccountId.get(a.id);
    rows.push(
      <tr key={a.id} style={{ opacity: a.isActive ? 1 : 0.55 }}>
        <td style={{ fontFamily: 'ui-monospace, monospace' }}>{a.code}</td>
        <td style={{ fontWeight: 500, paddingLeft: 12 + depth * 22 }}>
          {a.name}
          {bank && (
            <Link
              to="/bank"
              title={`Connected to bank feed: ${bank.accountName}`}
              style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, textDecoration: 'none', verticalAlign: 'middle' }}
              className="tag ok"
            >
              <BankIcon /> Connected
            </Link>
          )}
        </td>
        <td>{humanizeLabel(a.accountType)}</td>
        <td>{a.accountSubtype ? humanizeLabel(a.accountSubtype) : '—'}</td>
        <td>{humanizeLabel(a.normalBalance)}</td>
        <td className="right">
          <Link
            to="/reports/general-ledger"
            search={{ account: a.id, from: '2000-01-01' }}
            className="amount-link"
            style={{ color: parseFloat(a.balance) === 0 ? 'var(--text-very-faint)' : undefined }}
          >
            {formatCurrency(a.balance, true)}
          </Link>
        </td>
        <td>
          {!a.isActive ? (
            <span className="tag bad">inactive</span>
          ) : !a.allowDirectPosting ? (
            <span className="tag warn">header</span>
          ) : a.isSystem ? (
            <span className="tag">system</span>
          ) : (
            <span className="tag ok">active</span>
          )}
        </td>
        <td>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            {/* Only balance-sheet accounts can be tied to an outside statement.
              * Flagged accounts are the ONLY ones offered on the reconcile
              * screen, which is what stops a reconciliation being started
              * against the wrong account. */}
            {a.isActive &&
              a.allowDirectPosting &&
              (a.accountType === 'asset' || a.accountType === 'liability') && (
                <button
                  className={a.isReconcilable ? 'btn-primary' : 'btn-secondary'}
                  disabled={setReconcilable.isPending}
                  title={
                    a.isReconcilable
                      ? 'This account appears on the reconcile screen. Click to remove it.'
                      : 'Mark this account reconcilable so it appears on the reconcile screen.'
                  }
                  onClick={() =>
                    setReconcilable.mutate({
                      id: a.id,
                      input: { isReconcilable: !a.isReconcilable },
                    })
                  }
                >
                  {a.isReconcilable ? '✓ Reconcilable' : 'Reconcilable account'}
                </button>
              )}
            {/* Same idea as Reconcilable: only flagged accounts are offered as
              * the "pay from" account when writing a check, so the whole chart
              * never comes down and a payment cannot be drawn on the wrong
              * account by accident. */}
            {a.isActive && a.allowDirectPosting && a.accountType === 'asset' && (
              <button
                className={a.allowCheckWriting ? 'btn-primary' : 'btn-secondary'}
                disabled={setCheckWriting.isPending}
                title={
                  a.allowCheckWriting
                    ? 'Checks may be written against this account. Click to stop that.'
                    : 'Allow checks to be written against this account so it appears in the check writer.'
                }
                onClick={() =>
                  setCheckWriting.mutate({
                    id: a.id,
                    input: { allowCheckWriting: !a.allowCheckWriting },
                  })
                }
              >
                {a.allowCheckWriting ? '✓ Writes checks' : 'Writes checks'}
              </button>
            )}
            <button
              className="btn-secondary"
              onClick={() => {
                setEditingId(a.id);
                setShowForm(false);
              }}
            >
              Edit
            </button>
            {!a.isSystem &&
              (a.isActive ? (
                <button
                  className="btn-secondary"
                  disabled={deactivate.isPending}
                  onClick={() => {
                    if (confirm(`Deactivate "${a.name}"? It stays in reports but can't be posted to.`)) {
                      deactivate.mutate(a.id);
                    }
                  }}
                >
                  Deactivate
                </button>
              ) : (
                <button
                  className="btn-secondary"
                  disabled={reactivate.isPending}
                  onClick={() => reactivate.mutate({ id: a.id, input: { isActive: true } })}
                >
                  Reactivate
                </button>
              ))}
            {!a.isSystem && (
              <button
                className="btn-danger"
                disabled={del.isPending}
                title="Permanently delete this account (only if it has no transactions)"
                onClick={() => {
                  if (
                    confirm(
                      `Permanently delete "${a.code} ${a.name}"?\n\nThis cannot be undone. It will be refused if the account has any posted transactions, subaccounts, or other references — deactivate it instead in that case.`,
                    )
                  ) {
                    del.mutate(a.id, {
                      onError: (e) => {
                        const msg =
                          e instanceof ApiError
                            ? (e.body as { error?: string } | null)?.error ?? 'Delete failed'
                            : 'Delete failed';
                        alert(msg);
                      },
                    });
                  }
                }}
              >
                Delete
              </button>
            )}
          </div>
        </td>
      </tr>,
    );
    for (const c of kids) emit(c, depth + 1);
    if (kids.length > 0) {
      rows.push(
        <tr key={`${a.id}:total`} style={{ background: 'var(--bg-surface-2)' }}>
          <td></td>
          <td style={{ fontWeight: 600, paddingLeft: 12 + depth * 22 }}>Total {a.name}</td>
          <td colSpan={3}></td>
          <td className="right" style={{ fontWeight: 600 }}>{formatCurrency(rolledUp(a, new Set()), true)}</td>
          <td colSpan={2}></td>
        </tr>,
      );
    }
  }
  for (const root of childrenOf.get(null) ?? []) emit(root, 0);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Chart of Accounts</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {accounts.filter((a) => a.isActive).length} active
            {accounts.some((a) => !a.isActive) ? ` · ${accounts.filter((a) => !a.isActive).length} inactive` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showOnlyActive} onChange={(e) => setShowOnlyActive(e.target.checked)} />
            Show only active
          </label>
          <Link
            to="/accounts/duplicates"
            className="btn-secondary"
            style={{ textDecoration: 'none' }}
            title="Compare two accounts and bulk-void duplicate entries (e.g. after an import landed on the wrong account)."
          >
            Find duplicates
          </Link>
          <button
            className="btn-secondary"
            disabled={clear.isPending}
            onClick={() => {
              if (confirm('Deactivate every non-system account that has no transactions? System accounts and accounts with activity are kept.')) {
                clear.mutate();
              }
            }}
          >
            {clear.isPending ? 'Clearing…' : 'Clear chart of accounts'}
          </button>
          {!showForm && !editingAccount && (
            <button className="btn-primary" onClick={() => setShowForm(true)}>
              + New account
            </button>
          )}
        </div>
      </div>

      {clear.data && (
        <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13 }}>
          Deactivated <strong>{clear.data.deactivated}</strong> account{clear.data.deactivated === 1 ? '' : 's'}.
          Kept {clear.data.keptSystem} system and {clear.data.keptInUse} with activity.
        </div>
      )}

      {showForm && <AccountForm onClose={() => setShowForm(false)} companyId={companyId} accounts={accounts} />}
      {editingAccount && (
        <AccountForm
          key={editingAccount.id}
          onClose={() => setEditingId(null)}
          companyId={companyId}
          accounts={accounts}
          account={editingAccount}
        />
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load accounts.</p>}

      {accounts.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 100 }}>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Subtype</th>
              <th>Normal</th>
              <th className="right">Balance</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      )}
    </div>
  );
}

function AccountForm({
  onClose,
  companyId,
  accounts,
  account,
}: {
  onClose: () => void;
  companyId: string;
  accounts: AccountWithBalance[];
  /** When present, the form edits this account instead of creating a new one. */
  account?: AccountWithBalance;
}) {
  const isEdit = !!account;
  const create = useCreateAccount(companyId);
  const update = useUpdateAccount(companyId);
  const mutation = isEdit ? update : create;

  const [code, setCode] = useState(account?.code ?? '');
  const [name, setName] = useState(account?.name ?? '');
  const [accountType, setAccountType] = useState<AccountType>(account?.accountType ?? 'expense');
  const [accountSubtype, setAccountSubtype] = useState(account?.accountSubtype ?? '');
  const [parentAccountId, setParentAccountId] = useState(account?.parentAccountId ?? '');
  const [isHeader, setIsHeader] = useState(account ? !account.allowDirectPosting : false);
  const [isReconcilable, setIsReconcilable] = useState(account?.isReconcilable ?? false);
  const [cashFlowCategory, setCashFlowCategory] = useState<CashFlowCategory | ''>(
    account ? ((account.cashFlowCategory as CashFlowCategory | null) ?? '') : 'operating',
  );

  // An account can't be its own parent or a descendant's parent (would cycle).
  const disallowedParents = useMemo(() => {
    const out = new Set<string>();
    if (!account) return out;
    const kids = new Map<string, string[]>();
    for (const a of accounts) {
      if (a.parentAccountId) {
        if (!kids.has(a.parentAccountId)) kids.set(a.parentAccountId, []);
        kids.get(a.parentAccountId)!.push(a.id);
      }
    }
    out.add(account.id);
    const stack = [account.id];
    while (stack.length) {
      const id = stack.pop()!;
      for (const c of kids.get(id) ?? []) if (!out.has(c)) { out.add(c); stack.push(c); }
    }
    return out;
  }, [account, accounts]);

  const subtypes = ACCOUNT_SUBTYPES[accountType] ?? [];
  const isParentSelectable = (a: Account) =>
    a.accountType === accountType && a.isActive && !disallowedParents.has(a.id);
  const hasAnyParentOption = accounts.some(isParentSelectable);

  function onTypeChange(t: AccountType) {
    setAccountType(t);
    setAccountSubtype(''); // subtype list changed
    setParentAccountId(''); // parent must match the new type
  }

  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : mutation.error?.message;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const input = {
      code: code.trim() || undefined, // blank => auto (create) / unchanged (edit)
      name: name.trim(),
      accountType,
      accountSubtype: accountSubtype || null,
      parentAccountId: parentAccountId || null,
      normalBalance: NORMAL_BALANCE_MAP[accountType],
      cashFlowCategory: cashFlowCategory || undefined,
      allowDirectPosting: !isHeader,
      // Only meaningful on balance-sheet accounts; the field is hidden otherwise
      // and must not silently flag a revenue/expense account.
      isReconcilable:
        accountType === 'asset' || accountType === 'liability' ? isReconcilable : false,
    };
    if (isEdit) {
      update.mutate({ id: account!.id, input }, { onSuccess: () => onClose() });
    } else {
      create.mutate({ ...input, isSystem: false }, { onSuccess: () => onClose() });
    }
  }

  return (
    <form className="card" style={{ marginBottom: 16 }} onSubmit={submit}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>
        {isEdit ? `Edit ${account!.name}` : 'New account'}
      </h2>
      <div className="form-grid">
        <label htmlFor="code">Code</label>
        <input
          id="code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. 1001 (blank = auto)"
          autoFocus
          style={{ fontFamily: 'ui-monospace, monospace', maxWidth: 200 }}
        />

        <label htmlFor="name">Name</label>
        <input id="name" value={name} onChange={(e) => setName(e.target.value)} required />

        <label htmlFor="type">Type</label>
        <select id="type" value={accountType} onChange={(e) => onTypeChange(e.target.value as AccountType)}>
          {ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>
              {humanizeLabel(t)} ({humanizeLabel(NORMAL_BALANCE_MAP[t])}-normal)
            </option>
          ))}
        </select>

        <label htmlFor="subtype">Subtype</label>
        <select id="subtype" value={accountSubtype} onChange={(e) => setAccountSubtype(e.target.value)}>
          <option value="">— none —</option>
          {subtypes.map((s) => (
            <option key={s} value={s}>
              {humanizeLabel(s)}
            </option>
          ))}
        </select>

        <label htmlFor="parent">Parent account</label>
        <AccountPicker
          id="parent"
          accounts={accounts}
          value={parentAccountId}
          onChange={setParentAccountId}
          predicate={isParentSelectable}
          placeholder="No parent (top level)"
          noneLabel="No parent (top level)"
        />

        <label htmlFor="header">Header / total</label>
        <label htmlFor="header" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
          <input id="header" type="checkbox" checked={isHeader} onChange={(e) => setIsHeader(e.target.checked)} />
          <span className="muted" style={{ fontSize: 13 }}>
            Roll-up header — no direct posting; shows a “Total” of its sub-accounts
          </span>
        </label>

        {(accountType === 'asset' || accountType === 'liability') && (
          <>
            <label htmlFor="reconcilable">Reconcilable</label>
            <label
              htmlFor="reconcilable"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}
            >
              <input
                id="reconcilable"
                type="checkbox"
                checked={isReconcilable}
                onChange={(e) => setIsReconcilable(e.target.checked)}
              />
              <span className="muted" style={{ fontSize: 13 }}>
                Tied to an outside statement — only these accounts appear on the reconcile screen
              </span>
            </label>
          </>
        )}

        <label htmlFor="cf">Cash flow</label>
        <select
          id="cf"
          value={cashFlowCategory}
          onChange={(e) => setCashFlowCategory(e.target.value as CashFlowCategory | '')}
        >
          <option value="">— none —</option>
          <option value="operating">Operating</option>
          <option value="investing">Investing</option>
          <option value="financing">Financing</option>
        </select>
      </div>

      {!isEdit && !hasAnyParentOption && (
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          Tip: to make sub-accounts (e.g. Checking → Bank A, Bank B), first create the parent (mark it
          “Header / total”), then create each sub-account and pick it as the Parent account.
        </p>
      )}

      {errorMessage && (
        <div className="field-error" style={{ marginTop: 12 }}>
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button type="submit" className="btn-primary" disabled={mutation.isPending || !name.trim()}>
          {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create account'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
